import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { impersonationApi } from '@/services/api';
import toast from 'react-hot-toast';
import { logger } from '@/utils/logger';
import { resolveImpersonationStartError } from '@/utils/impersonationError';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@/utils/apiError';

interface ImpersonatedUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  userType: string;
}

interface ImpersonationContextType {
  isImpersonating: boolean;
  impersonatedUser: ImpersonatedUser | null;
  originalAdmin: { email: string } | null;
  startImpersonation: (userId: string, reason?: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
  isLoading: boolean;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error('useImpersonation must be used within ImpersonationProvider');
  }
  return context;
};

interface ImpersonationProviderProps {
  children: ReactNode;
}

export const ImpersonationProvider: React.FC<ImpersonationProviderProps> = ({ children }) => {
  const { t } = useTranslation('toasts');
  const queryClient = useQueryClient();
  // Restore impersonation state from sessionStorage (survives page reloads)
  const [isImpersonating, setIsImpersonating] = useState(() => {
    return sessionStorage.getItem('impersonation_active') === 'true';
  });
  const [impersonatedUser, setImpersonatedUser] = useState<ImpersonatedUser | null>(() => {
    const stored = sessionStorage.getItem('impersonated_user');
    return stored ? JSON.parse(stored) : null;
  });
  const [originalAdmin, setOriginalAdmin] = useState<{ email: string } | null>(() => {
    const stored = sessionStorage.getItem('original_admin');
    return stored ? JSON.parse(stored) : null;
  });
  const [isLoading, setIsLoading] = useState(false);

  // Reconcile sessionStorage with the server's view of impersonation state.
  // sessionStorage survives page reloads but it can drift from the cookie:
  // the access-token cookie can be rotated to a non-impersonation token
  // (e.g. when the refresh-token flow generates a regular admin access
  // token, which doesn't carry the isImpersonating claim, or when the
  // 4h impersonation JWT expires and the browser falls back to the
  // longer-lived admin session in another tab). When that happens
  // sessionStorage says "impersonating" but the cookie no longer is —
  // banner shows, Stop misbehaves, the admin gets stuck.
  //
  // Strategy: on mount AND on window focus, ask the server. If the
  // server reports no active impersonation session for the caller but
  // the client thinks otherwise, clear local state so the banner
  // disappears and the rail/page render as the actual cookie identity
  // (admin). If the server says we ARE impersonating but client lost
  // the impersonatedUser metadata, rehydrate it from the session payload.
  useEffect(() => {
    let cancelled = false;
    const reconcile = async () => {
      try {
        const status = await impersonationApi.getImpersonationStatus();
        if (cancelled) return;
        const serverSession = (status as any)?.session ?? null;
        const serverIsImpersonating = !!serverSession;
        if (!serverIsImpersonating && sessionStorage.getItem('impersonation_active') === 'true') {
          logger.info('Reconciling impersonation: server has no active session, clearing local state');
          setIsImpersonating(false);
          setImpersonatedUser(null);
          setOriginalAdmin(null);
          sessionStorage.removeItem('impersonation_active');
          sessionStorage.removeItem('impersonated_user');
          sessionStorage.removeItem('original_admin');
          queryClient.clear();
        } else if (serverIsImpersonating && sessionStorage.getItem('impersonation_active') !== 'true') {
          // Less common but possible: cookie carries an impersonation
          // token while sessionStorage is empty (e.g. opened a fresh tab
          // mid-impersonation, or storage was wiped). Rehydrate from
          // the server payload so the banner appears.
          const target = serverSession.targetUser;
          if (target) {
            const restored = {
              id: target.id,
              email: target.email,
              firstName: target.firstName,
              lastName: target.lastName,
              userType: target.role || 'user',
            };
            setIsImpersonating(true);
            setImpersonatedUser(restored);
            sessionStorage.setItem('impersonation_active', 'true');
            sessionStorage.setItem('impersonated_user', JSON.stringify(restored));
          }
        }
      } catch (err) {
        // Server unreachable or 401 — don't touch local state. A 401
        // is already handled by the axios interceptor which redirects
        // to /login; anything else is transient and reconciliation
        // will retry on next focus.
        logger.warn('Impersonation reconciliation failed', err as any);
      }
    };
    reconcile();
    const onFocus = () => { reconcile(); };
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startImpersonation = async (userId: string, reason?: string) => {
    // Prevent nested impersonation
    if (isImpersonating) {
      toast.error(t('error.alreadyImpersonating', 'Cannot impersonate while already impersonating. Please return to your admin account first.'));
      return;
    }

    setIsLoading(true);
    try {
      const response = await impersonationApi.startImpersonation(userId, reason);

      // The server sets the token as an httpOnly cookie — no client-side cookie write needed.
      // Update local state and persist to sessionStorage (survives page reload).
      setIsImpersonating(true);
      setImpersonatedUser(response.impersonatedUser);
      sessionStorage.setItem('impersonation_active', 'true');
      sessionStorage.setItem('impersonated_user', JSON.stringify(response.impersonatedUser));
      if (response.originalAdmin) {
        setOriginalAdmin(response.originalAdmin);
        sessionStorage.setItem('original_admin', JSON.stringify(response.originalAdmin));
      }

      toast.success(t('success.nowImpersonating', { email: response.impersonatedUser.email }));

      // Clear React Query cache to ensure fresh data is fetched for impersonated user
      queryClient.clear();

      // Reload page to reflect new permissions
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 500);
    } catch (error: any) {
      logger.error('Failed to start impersonation', error);
      // #1323 — surface the backend's reason (by IMPERSONATION_* code), not a
      // blanket "Failed to start impersonation". The api client rejects with
      // the parsed body, so `error.response` never exists here.
      //
      // Toast id: 403 refusals (target is an admin / self) also trip the api
      // client's global "You do not have permission" toast, which is
      // misleading for this case. Reusing its stable id (`api-403`) replaces
      // that generic toast in place with the specific reason instead of
      // stacking two contradictory toasts.
      toast.error(resolveImpersonationStartError(error, t), { id: 'api-403' });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const stopImpersonation = async () => {
    setIsLoading(true);
    try {
      await impersonationApi.stopImpersonation();

      // The server replaces the httpOnly cookie with the admin token — no client-side write needed.
      setIsImpersonating(false);
      setImpersonatedUser(null);
      setOriginalAdmin(null);
      sessionStorage.removeItem('impersonation_active');
      sessionStorage.removeItem('impersonated_user');
      sessionStorage.removeItem('original_admin');

      toast.success(t('success.returnedToAdmin'));

      // Clear React Query cache to ensure fresh data is fetched for admin user
      queryClient.clear();

      // Reload page to reflect admin permissions
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 500);
    } catch (error: any) {
      logger.error('Failed to stop impersonation', error, {
        status: error.response?.status,
        body: error.response?.data,
      });

      // Escape hatch: ANY backend error during stop should still let the
      // admin exit impersonation. Previously only a 404 ("session
      // expired") cleared local state and redirected; a 500 from the
      // controller's catch block — e.g. a transient Redis or DB hiccup
      // while ending the session — left the admin stuck on the
      // impersonated view with a red toast and no recovery short of
      // manually clearing cookies. Treat every error the same as 404:
      // scrub local state, surface a soft toast, and bounce to /login
      // so the admin can log back in cleanly. Toast tone varies by
      // status (info vs error) so the failure mode stays visible.
      const status = error.response?.status;
      const friendly =
        status === 404
          ? t('info.sessionExpired', 'Session expired. Please log in again.')
          : apiErrorMessage(error, t('error.impersonationEndFailed'));

      setIsImpersonating(false);
      setImpersonatedUser(null);
      setOriginalAdmin(null);
      sessionStorage.removeItem('impersonation_active');
      sessionStorage.removeItem('impersonated_user');
      sessionStorage.removeItem('original_admin');
      queryClient.clear();

      if (status === 404) {
        toast(friendly);
      } else {
        toast.error(friendly);
      }

      setTimeout(() => {
        window.location.href = '/login';
      }, 500);
      return;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ImpersonationContext.Provider
      value={{
        isImpersonating,
        impersonatedUser,
        originalAdmin,
        startImpersonation,
        stopImpersonation,
        isLoading,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
};
